// v2.20.2 · FICHA EKH por cama, organizada en pestañas: Telemetría (FC/FR y curvas vivas primero),
// Actividad (permanencia, entradas y salidas, histórico), Casos e Información. Lo técnico del OEM
// queda abajo y plegado: el cuerpo clínico se lee desde un móvil sin desplazamiento infinito.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, BedDouble, Activity, Clock, DoorOpen } from "lucide-react";
import api from "@/lib/api";
import { CurvaAmplitud, TendenciaFrecuencia } from "@/components/ekh/CurvasEkh";
import EscenaRadarCama from "@/components/ekh/EscenaRadarCama";
import useCurvaViva from "@/hooks/useCurvaViva";
import { segmentosSerie, duracionTexto, edadTexto, valorFrecuencia, estadoCama, CALIDAD_TEXTO } from "@/lib/ekh/vista";

const METRICAS = "heart_rate,respiratory_rate,heart_rate_avg,respiratory_rate_avg,heart_amplitude,respiratory_amplitude";
const REFRESCO_MS = 5000;
// La curva viva se pide cada segundo con cursor por sesión/secuencia: avanza con muestras reales.
const CURVA_MS = 1000;
const VENTANA_VIVA = 256;
// Ventana de consulta del detalle. Más amplia que la del mosaico para que una pausa del canal no
// deje la pantalla sin contexto; los huecos se siguen declarando, nunca se rellenan.
const VENTANA_HORAS = 6;

const ESTADO_LABEL = { en_cama: "En cama", fuera_de_cama: "Fuera de cama", desconocido: "Desconocido" };
const TABS = [
  ["telemetria", "Telemetría"],
  ["actividad", "Actividad"],
  ["casos", "Casos"],
  ["info", "Información"],
];
const PRIORIDAD_TONO = { maxima: "gv2-chip-alert", alta: "gv2-chip-alert", media: "gv2-chip-amber" };

function Dato({ icono: Icono, label, valor, testId, title }) {
  return (
    <div data-testid={testId} title={title} className="gv2-panel min-w-0 rounded-lg px-2 py-1.5">
      <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        <Icono className="h-3 w-3 shrink-0" />{label}
      </p>
      <p className="truncate text-sm font-extrabold tabular-nums text-slate-800 dark:text-slate-100">
        {valor}
      </p>
    </div>
  );
}

function Fila({ label, valor }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="shrink-0 text-[11px] text-slate-400">{label}</span>
      <span className="min-w-0 truncate text-right text-[11px] font-semibold text-slate-600 dark:text-slate-300">
        {valor || "—"}
      </span>
    </div>
  );
}

export default function EkhDetalleCama({ fila, onCerrar, casos = null, onAbrirCaso = null }) {
  const [series, setSeries] = useState(null);
  const [actividad, setActividad] = useState(null);
  const [error, setError] = useState(false);
  const [sinAcceso, setSinAcceso] = useState(false);
  const [tab, setTab] = useState("telemetria");
  const camaId = fila?.cama_id;
  // P1 · cerca de identidad: una respuesta en vuelo de la cama ANTERIOR no puede repintar la
  // cama vigente (cambios rápidos entre camas con una respuesta tardía).
  const vigenteRef = useRef(camaId);
  const { vivo, sinAcceso: sinAccesoCurva } = useCurvaViva(camaId, {
    activo: !sinAcceso, intervaloMs: CURVA_MS, ventana: VENTANA_VIVA });

  const cargar = useCallback(async () => {
    if (!camaId) return;
    try {
      const desde = new Date(Date.now() - VENTANA_HORAS * 3600 * 1000).toISOString();
      const [s, a] = await Promise.all([
        api.get(`/ekh/vista/camas/${camaId}/series`, { params: { metricas: METRICAS, limite: 900, desde } }),
        api.get(`/ekh/vista/camas/${camaId}/actividad`, { params: { desde } }),
      ]);
      if (vigenteRef.current !== camaId) return;
      if (s.data?.cama_id && s.data.cama_id !== camaId) return;
      setSeries(s.data);
      setActividad(a.data);
      setError(false);
    } catch (e) {
      if (vigenteRef.current !== camaId) return;
      const st = e?.response?.status;
      if (st === 403 || st === 404) setSinAcceso(true);   // H5 · se termina la consulta
      setError(true);
    }
  }, [camaId]);

  useEffect(() => {
    // Al cambiar de cama no se arrastra el estado de la anterior.
    vigenteRef.current = camaId;
    setSeries(null);
    setActividad(null);
    setError(false);
    setSinAcceso(false);
    setTab("telemetria");
  }, [camaId]);

  useEffect(() => {
    if (sinAcceso) return;
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, [cargar, sinAcceso]);

  // Último tramo persistido: sólo respalda la curva mientras no llega la primera cola en vivo.
  const curva = useMemo(() => {
    const bs = [...(series?.bloques || [])].sort((a, b) => (a.seq_inicio ?? 0) - (b.seq_inicio ?? 0));
    const h = [];
    const r = [];
    const huecos = [];
    for (const b of bs.slice(-4)) {
      h.push(...(b.muestras?.heart_amplitude || []));
      r.push(...(b.muestras?.respiratory_amplitude || []));
      huecos.push(...(b.huecos || []));
    }
    let fin = -1;
    for (let i = h.length - 1; i >= 0; i -= 1) {
      if (h[i] || r[i]) { fin = i; break; }
    }
    const corte = fin < 0 ? h.length : fin + 1;
    const ini = Math.max(0, corte - 256);
    return {
      heart_amplitude: h.slice(ini, corte),
      respiratory_amplitude: r.slice(ini, corte),
      muestras: corte - ini,
      huecos,
    };
  }, [series]);

  const casosCama = useMemo(() => {
    if (!Array.isArray(casos)) return null;
    return casos.filter((c) => {
      const u = c.ubicacion || {};
      if (u.nivel === "cama" && u.cama_id && u.cama_id === camaId) return true;
      return Boolean(fila?.persona_id) && c.persona_id === fila.persona_id;
    });
  }, [casos, camaId, fila]);

  if (!fila) return null;
  const presencia = fila.instantaneas?.presence?.valor;
  const estado = estadoCama(fila);
  const enVivo = Boolean(vivo?.heart?.length || vivo?.resp?.length);
  const amplitudCardiaca = enVivo ? vivo.heart : curva.heart_amplitude;
  const amplitudRespiratoria = enVivo ? vivo.resp : curva.respiratory_amplitude;
  const sinSenalCardiaca = amplitudCardiaca.length > 0 && amplitudCardiaca.every((v) => !v);
  const sinSenalRespiratoria = amplitudRespiratoria.length > 0 && amplitudRespiratoria.every((v) => !v);
  // Edad de la ÚLTIMA muestra recibida: con la ventana llena es lo único que delata que la curva
  // sigue viva (el conteo se queda en 256).
  const ultimaMuestra = vivo?.t?.length ? Date.parse(vivo.t[vivo.t.length - 1]) : null;
  const edadUltima = Number.isFinite(ultimaMuestra)
    ? Math.max(0, Math.round((Date.now() - ultimaMuestra) / 1000)) : null;
  // «En vivo» exige una muestra RECIENTE: un tramo de hace horas se dibuja, pero no se anuncia
  // como señal en curso.
  const vivoReciente = enVivo && edadUltima != null && edadUltima <= 10;
  // v2.21.6 · el selector de equipo puede no resolver un EKH (cama con EKT) o encontrar VARIOS:
  // se declara en la ficha en vez de presentarlo como «sin muestras».
  const razonSelector = series?.lectura?.razon || vivo?.razon || null;
  const equiposEkh = series?.lectura?.equipos_ekh || [];
  const avisoSelector = razonSelector === "sin_equipo_ekh_en_la_cama"
    ? { testId: "ekh-detalle-sin-ekh", texto: "Esta cama no tiene un radar EKH asignado: las curvas y el histórico de signos no aplican." }
    : razonSelector === "varios_equipos_ekh_en_la_cama"
      ? { testId: "ekh-detalle-ekh-ambiguo",
          texto: `Hay ${equiposEkh.length || "varios"} radares EKH asignados a esta cama al mismo tiempo. No se elige ninguno: revise la asignación de equipos.` }
      : null;
  const fc = valorFrecuencia(fila.instantaneas?.heart_rate);
  const fr = valorFrecuencia(fila.instantaneas?.respiratory_rate);
  const mov = fila.ultimo_movimiento;
  const hr = fila.instantaneas?.heart_rate;

  return (
    <aside data-testid="ekh-detalle-cama" className="gv2-card w-full min-w-0 max-w-full rounded-2xl p-3 pb-16 lg:w-[420px] lg:max-w-[420px] lg:shrink-0 lg:pb-24">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-extrabold text-[#012472] dark:text-slate-100">
            {fila.persona_nombre || "Sin residente acreditado"}
          </h3>
          <p className="truncate text-[11px] text-slate-500">
            {[fila.cama_etiqueta || fila.equipo, fila.habitacion].filter(Boolean).join(" · ") || "Sin ubicación"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span data-testid="ekh-detalle-estado" className={`gv2-chip ${estado.tono}`}>{estado.texto}</span>
          <button type="button" data-testid="ekh-detalle-cerrar" onClick={onCerrar}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {(sinAcceso || sinAccesoCurva) ? (
        <p data-testid="ekh-detalle-sin-acceso" className="mt-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          No tienes acceso al detalle de esta cama.
        </p>
      ) : error && (
        <p data-testid="ekh-detalle-error" className="mt-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          No se pudo consultar el detalle en este ciclo.
        </p>
      )}

      {avisoSelector && (
        <p data-testid={avisoSelector.testId} data-razon={razonSelector}
          className="mt-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-800">
          {avisoSelector.texto}
        </p>
      )}

      <div data-testid="ekh-detalle-tabs" role="tablist"
        className="mt-2 flex gap-0.5 rounded-xl bg-[color:var(--gv-raised,#eef2f7)] p-1">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id}
            data-testid={`ekh-tab-${id}`} onClick={() => setTab(id)}
            className={`min-h-8 flex-1 whitespace-nowrap rounded-lg px-1.5 text-[10px] font-extrabold uppercase tracking-tight transition-colors sm:px-2 sm:text-[11px] sm:tracking-wide ${
              tab === id ? "bg-white text-[#012472] shadow-sm dark:bg-[#192C47] dark:text-[#80B3FF]" : "text-slate-500"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "telemetria" && (
        <div data-testid="ekh-panel-telemetria" className="mt-2 space-y-2">
          <div data-testid="ekh-detalle-vitales" className="grid grid-cols-2 gap-2">
            {[["Frecuencia cardiaca", fc, "lpm", "ekh-detalle-fc", fila.instantaneas?.heart_rate],
              ["Frecuencia respiratoria", fr, "rpm", "ekh-detalle-fr", fila.instantaneas?.respiratory_rate]].map(
              ([label, v, unidad, id, m]) => (
                <div key={id} data-testid={id} data-calidad={m?.calidad || "sin_dato"}
                  className="gv2-panel rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="flex items-baseline gap-1">
                    <span className={`font-extrabold tabular-nums ${v.dato ? "text-4xl text-slate-800 dark:text-slate-100" : "text-sm text-slate-400"}`}>
                      {v.texto}
                    </span>
                    {v.dato && <span className="text-[11px] font-semibold text-slate-400">{unidad}</span>}
                  </p>
                  <p className="truncate text-[10px] text-slate-400">
                    {m?.edad_seg != null ? edadTexto(m.edad_seg) : v.leyenda}
                  </p>
                </div>
              ))}
          </div>

          {avisoSelector ? (
            <p data-testid="ekh-curvas-no-aplican" className="gv2-panel rounded-xl px-3 py-4 text-center text-xs text-slate-400">
              Las curvas no aplican a esta cama: no hay un radar EKH seleccionable.
            </p>
          ) : (
            <>
              <CurvaAmplitud testId="ekh-curva-cardiaca" titulo="Latido" color="#FF453A"
                muestras={amplitudCardiaca} sinSenal={sinSenalCardiaca} enVivo={vivoReciente} />
              <CurvaAmplitud testId="ekh-curva-respiratoria" titulo="Respiración" color="#0A84FF"
                muestras={amplitudRespiratoria} sinSenal={sinSenalRespiratoria} enVivo={vivoReciente} />
            </>
          )}

          <div data-testid="ekh-detalle-presencia" className="grid grid-cols-2 gap-1.5">
            <Dato icono={BedDouble} label="Presencia" testId="ekh-dato-presencia"
              valor={presencia == null ? "Sin dato" : presencia ? "En cama" : "Fuera de cama"} />
            <Dato icono={Activity} label="Último movimiento" testId="ekh-dato-movimiento"
              title="Eventos de movimiento recibidos del equipo."
              valor={mov?.edad_seg != null ? edadTexto(mov.edad_seg) : "Sin evento"} />
            <Dato icono={Clock} label="Tiempo en cama" testId="ekh-dato-en-cama"
              valor={duracionTexto(actividad?.totales?.en_cama_seg)} />
            <Dato icono={DoorOpen} label="Fuera de cama" testId="ekh-dato-fuera"
              valor={duracionTexto(actividad?.totales?.fuera_de_cama_seg)} />
          </div>

          <details data-testid="ekh-tecnico-detalle" className="gv2-panel rounded-xl px-3 py-2">
            <summary className="cursor-pointer text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
              Detalle técnico del sensor
            </summary>
            <div className="mt-1.5">
              <Fila label="Muestras en pantalla"
                valor={`${amplitudCardiaca.filter((v) => typeof v === "number").length}`} />
              <Fila label="Última muestra" valor={edadUltima != null ? edadTexto(edadUltima) : "—"} />
              <Fila label="Cadencia" valor={vivo?.cadencia_ms ? `${vivo.cadencia_ms} ms` : "—"} />
              <Fila label="Interrupciones de trama" valor={`${(vivo?.huecos || curva.huecos || []).length}`} />
              <Fila label="Fecha del equipo"
                valor={`${hr?.raw_device_datetime || "—"}${hr?.reloj_dispositivo_valido === false ? " (no acreditada)" : ""}`} />
              <Fila label="Recepción" valor={hr?.received_at || "—"} />
              <Fila label="Estado de la lectura" valor={CALIDAD_TEXTO[hr?.calidad] || "Sin estado"} />
              <p className="mt-1 text-[10px] text-slate-400">
                Las curvas muestran la amplitud firmada por el fabricante, sin unidad acreditada, y
                conservan ceros y huecos. No son un ECG ni un diagnóstico.
              </p>
            </div>
          </details>
        </div>
      )}

      {tab === "actividad" && (
        <div data-testid="ekh-panel-actividad" className="mt-2 space-y-2">
          <div data-testid="ekh-actividad-totales" className="grid grid-cols-3 gap-1.5">
            {[["En cama", actividad?.totales?.en_cama_seg, "ekh-total-en-cama"],
              ["Fuera de cama", actividad?.totales?.fuera_de_cama_seg, "ekh-total-fuera"],
              ["Sin dato", actividad?.totales?.desconocido_seg, "ekh-total-desconocido"]].map(
              ([label, seg, id]) => (
                <div key={id} data-testid={id} className="gv2-panel rounded-lg px-2 py-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="text-sm font-extrabold tabular-nums text-slate-800 dark:text-slate-100">
                    {duracionTexto(seg)}
                  </p>
                </div>
              ))}
          </div>

          <div data-testid="ekh-actividad-episodios" className="gv2-panel rounded-xl p-3">
            <p className="flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#012472]">
                Entradas y salidas
              </span>
              <span data-testid="ekh-mov-conteo" className="tabular-nums text-[10px] text-slate-400">
                {actividad?.movimiento?.eventos ?? 0} movimientos
              </span>
            </p>
            {!actividad?.episodios?.length ? (
              <p className="py-3 text-center text-xs text-slate-400">
                Sin entradas ni salidas en las últimas {VENTANA_HORAS} horas.
              </p>
            ) : (
              <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                {actividad.episodios.slice(-12).reverse().map((e, i) => (
                  <li key={i} data-estado={e.estado}
                    className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="truncate text-slate-600 dark:text-slate-300">
                      {ESTADO_LABEL[e.estado] || e.estado}
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-400">
                      {duracionTexto(e.duracion_seg)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-[10px] text-slate-400">
              Los tramos sin recepción se declaran sin dato: no se suman como permanencia.
            </p>
          </div>

          <p className="pt-1 text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
            Histórico de las últimas {VENTANA_HORAS} horas
          </p>
          <TendenciaFrecuencia testId="ekh-tendencia-fc" titulo="Frecuencia cardiaca"
            unidad="lpm" color="#FF453A" segmentos={segmentosSerie(series?.series?.heart_rate || [])} />
          <TendenciaFrecuencia testId="ekh-tendencia-fr" titulo="Frecuencia respiratoria"
            unidad="rpm" color="#0A84FF" segmentos={segmentosSerie(series?.series?.respiratory_rate || [])} />
        </div>
      )}

      {tab === "casos" && (
        <div data-testid="ekh-panel-casos" className="mt-2 space-y-2">
          {casosCama === null ? (
            <p data-testid="ekh-casos-no-disponible" className="gv2-panel rounded-xl px-3 py-4 text-center text-xs text-slate-400">
              Los casos de este residente se consultan en el carril de atención del centro.
            </p>
          ) : casosCama.length === 0 ? (
            <p data-testid="ekh-casos-vacio" className="gv2-panel rounded-xl px-3 py-4 text-center text-xs text-slate-400">
              Sin casos abiertos vinculados a esta cama.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {casosCama.map((c) => (
                <li key={c.alarma_id || c.id}>
                  <button type="button" data-testid={`ekh-caso-${c.alarma_id || c.id}`}
                    onClick={() => onAbrirCaso && onAbrirCaso(c)} disabled={!onAbrirCaso}
                    className="gv2-panel w-full rounded-xl px-3 py-2 text-left disabled:cursor-default">
                    <p className="flex items-center justify-between gap-2">
                      <span className="truncate text-[12px] font-extrabold text-slate-700 dark:text-slate-200">
                        {c.verbo || c.tipo || "Caso abierto"}
                      </span>
                      <span className={`gv2-chip shrink-0 ${PRIORIDAD_TONO[c.prioridad] || "gv2-chip-neutral"}`}>
                        {c.prioridad || "sin prioridad"}
                      </span>
                    </p>
                    <p className="truncate text-[11px] text-slate-400">
                      {c.estadoLabel || c.estado}{c.donde ? ` · ${c.donde}` : ""}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "info" && (
        <div data-testid="ekh-panel-info" className="mt-2 space-y-2">
          <div className="gv2-panel rounded-xl px-3 py-2">
            <Fila label="Residente" valor={fila.persona_nombre} />
            <Fila label="Cama" valor={fila.cama_etiqueta} />
            <Fila label="Habitación" valor={fila.habitacion} />
            <Fila label="Equipo" valor={fila.equipo} />
            <Fila label="Registro" valor={fila.registro} />
            <Fila label="Inicio del periodo" valor={fila.asignacion?.inicio_en} />
            {!fila.identidad_acreditada && (
              <Fila label="Identidad" valor={(fila.identidad_razon || "sin acreditar").replace(/_/g, " ")} />
            )}
          </div>
          <EscenaRadarCama presencia={presencia}
            movimientoReciente={(fila.ultimo_movimiento?.edad_seg ?? 1e9) < 120} />
        </div>
      )}
    </aside>
  );
}
